import React, { useCallback, useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import useClosePosition from "../../hooks/useClosePosition";
import { formatToCurrency } from "../../utils/money";

const AMOUNT_CHANGED = "The amount changed; please review it again.";

/**
 * Shows the exact payout before a position is closed, and closes it only
 * for that amount (addendum v1.5 S9). The figures are the server's; the
 * total is sent back with the close so a changed amount is refused rather
 * than paid unseen.
 */
const ClosePositionModal = ({ investment, onCancel, onClosed }) => {
  const { preview, close } = useClosePosition();
  const [payout, setPayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setPayout(null);
    try {
      setPayout(await preview(investment._id));
    } catch (err) {
      setError(err.response?.data?.message ?? "The payout could not be loaded. Please try again.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investment._id]);

  useEffect(() => {
    load();
  }, [load]);

  const confirm = async () => {
    setClosing(true);
    setError("");
    try {
      const res = await close(investment._id, payout.total);
      onClosed(res.data.message);
    } catch (err) {
      if (err.response?.data?.code === "CLOSE_AMOUNT_CHANGED") {
        setError(AMOUNT_CHANGED);
        await load();
      } else {
        setError(err.response?.data?.message ?? "The investment could not be closed. Please try again.");
      }
    } finally {
      setClosing(false);
    }
  };

  return (
    <div
      tabIndex="-1"
      aria-hidden="true"
      className="fixed top-0 left-0 right-0 bg-gray-700 bg-opacity-50 z-20 w-full p-4 overflow-x-hidden overflow-y-auto md:inset-0 h-full"
    >
      <div className="relative w-full h-full mx-auto top-[25%] max-w-xl md:h-auto">
        <div className="relative bg-[#181b20] z-[9999] opacity-100 rounded-lg shadow">
          <div className="flex items-start justify-between p-4 border-b border-b-gray-400 rounded-t">
            <h3 className="text-xl font-semibold text-gray-300">Close investment</h3>
          </div>
          <div className="px-6 py-5 text-gray-400">
            {loading ? (
              <div className="flex justify-center py-6">
                <CircularProgress color="inherit" size={25} />
              </div>
            ) : payout ? (
              <>
                <h1 className="font-medium text-lg md:text-xl text-center text-gray-300">
                  You will receive {formatToCurrency(Number(payout.total))}
                </h1>
                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Capital</span>
                    <span>{formatToCurrency(Number(payout.capital))}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Earnings</span>
                    <span>{formatToCurrency(Number(payout.earnings))}</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-600 pt-2 font-medium text-gray-300">
                    <span>Total</span>
                    <span>{formatToCurrency(Number(payout.total))}</span>
                  </div>
                </div>
              </>
            ) : null}
            {error && <p className="mt-4 text-sm text-red-400">{error}</p>}
          </div>
          <div className="flex items-center px-6 py-4 space-x-2 border-t border-gray-200 rounded-b">
            <button
              type="button"
              disabled={loading || closing || !payout}
              onClick={confirm}
              className="bg-[#b2ffce] rounded-md text-sm px-5 py-2 text-center disabled:opacity-50"
            >
              {closing ? <CircularProgress color="inherit" size={25} /> : "Confirm"}
            </button>
            <button
              type="button"
              disabled={closing}
              onClick={onCancel}
              className="rounded-md text-sm px-5 py-2 text-center text-gray-300 border border-gray-400"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClosePositionModal;
